import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import AHDate from "_shared/dates/date";
import MessageMarkdownContent from "_shared/_views/markdown/message-markdown-content";
import { useLazyGetMessageHistoryQuery } from "messaging/_api/messaging-api";
import MessageHistory from "messaging/message-history";
import Message from "messaging/message";
import React, { useState } from "react";

type MessageHistoryControlProps = {
  message: Message;
  label: "Deleted" | "Edited" | "Removed";
  /** Deleted and removed messages are only actionable in the author's (sent) view. */
  canViewHistory?: boolean;
  /** Proposal comments use a slightly larger status label than conversation messages. */
  surface?: "proposalComment";
};

type HistoryLabelStyleProps = {
  surface?: "proposalComment";
};

const HistoryLabel = styled("span", {
  shouldForwardProp: (prop) => prop !== "surface",
})<HistoryLabelStyleProps>(({ theme, surface }) => ({
  ...theme.typography.caption,
  fontSize: surface === "proposalComment" ? "0.9em" : "0.75em",
  fontStyle: "italic",
  lineHeight: 1.5,
  marginInline: theme.spacing(1),
}));

const HistoryButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "surface",
})<HistoryLabelStyleProps>(({ theme, surface }) => ({
  fontSize: surface === "proposalComment" ? "0.9em" : "0.75em",
  fontStyle: "italic",
  lineHeight: 1.5,
  marginInline: theme.spacing(1),
  minWidth: 0,
  padding: 0,
  textTransform: "none",
}));

const HistoryDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingRight: theme.spacing(6),
}));

const HistoryCloseButton = styled(IconButton)({
  position: "absolute",
  right: 8,
  top: 8,
});

const HistorySubject = styled("span")(({ theme }) => ({
  ...theme.typography.body2,
  whiteSpace: "pre-wrap",
}));

export default function MessageHistoryControl(props: MessageHistoryControlProps) {
  const { message, label, surface } = props;
  const canViewHistory = props.canViewHistory ?? true;
  const displayLabel = label === "Removed" ? label : "History";
  const [open, setOpen] = useState(false);
  const [getMessageHistory, historyQuery] = useLazyGetMessageHistoryQuery();
  const forumId = message.forum.id?.value;
  const messageId = message.id?.value;
  const canLoadHistory = canViewHistory && forumId !== undefined && messageId !== undefined;
  const isLoadingHistory = Boolean(open && (historyQuery.isUninitialized || historyQuery.isFetching));

  const handleOpen = () => {
    setOpen(true);
    if (canLoadHistory) void getMessageHistory({ forumId, messageId });
  };

  const handleClose = () => setOpen(false);

  if (!canViewHistory) {
    return (
      <HistoryLabel aria-label={label} surface={surface}>
        {displayLabel}
      </HistoryLabel>
    );
  }

  return (
    <>
      <HistoryButton
        type="button"
        variant="text"
        size="small"
        onClick={handleOpen}
        disabled={!canLoadHistory}
        aria-label={`View message history (${label})`}
        surface={surface}
      >
        {displayLabel}
      </HistoryButton>
      <MessageHistoryDialog
        open={open}
        onClose={handleClose}
        history={historyQuery.data}
        isLoading={isLoadingHistory}
        isError={historyQuery.isError}
      />
    </>
  );
}

type MessageHistoryDialogProps = {
  open: boolean;
  onClose: () => void;
  history?: MessageHistory[];
  isLoading: boolean;
  isError: boolean;
};

function MessageHistoryDialog({ open, onClose, history, isLoading, isError }: MessageHistoryDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="message-history-title">
      <HistoryDialogTitle id="message-history-title">
        Message history
        <HistoryCloseButton aria-label="Close message history" onClick={onClose}>
          <CloseIcon />
        </HistoryCloseButton>
      </HistoryDialogTitle>
      <DialogContent dividers>
        {isLoading && <CircularProgress aria-label="Loading message history" size={24} />}
        {isError && <Typography color="error">Unable to load message history.</Typography>}
        {!isLoading && !isError && history?.length === 0 && <Typography>No message history available.</Typography>}
        <List dense disablePadding>
          {history?.map((version, index) => (
            <ListItem key={version.id} disableGutters alignItems="flex-start" divider={index < history.length - 1}>
              <ListItemText
                primary={`Version ${index + 1}`}
                slots={{ secondary: "div" }}
                secondary={
                  <Stack component="div" spacing={0.5}>
                    <Typography component="span" variant="caption">
                      {version.updated ? "Updated" : "Created"} {formatHistoryDate(version.updated ?? version.created)}
                    </Typography>
                    <HistorySubject>
                      Subject: {version.subject || "(none)"}
                    </HistorySubject>
                    <MessageMarkdownContent content={version.content || "(no content)"} />
                    <Typography component="span" variant="body2">
                      Attachments ({version.attachmentCount}){version.attachments.length > 0 ? `: ${version.attachments.map((attachment) => attachment.name).join(", ")}` : ""}
                    </Typography>
                  </Stack>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function formatHistoryDate(value: AHDate | string): string {
  if (typeof value === "string") return new AHDate(value).format("MM/DD/YY hh:mm A");
  return value.format("MM/DD/YY hh:mm A");
}
